import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackUserEvent } from '../analytics';

export const EVENT_IDS = {
  DOWNLOAD_INVOICES: '2821',
  DOWNLOAD_CSV: '2835',
  SET_FILTER: '2836',
  SEARCH_BY_INVOICE_NUMBER: '2840',
  REMOVE_PAYMENT_METHOD: '3298',
  ADD_CREDIT_CARD: '3178',
  ADD_ACH: '3198',
  ADD_AUTOPAY: '3288',
  PAY_OFFLINE: '3310',
  REMOVE_AUTOPAY: '3292',
  OTHER_AMOUNT: '3300',
  EDIT_BILLING_CONTACT_INFO: '4062',
  SAVE_CLOSE_BILLING_CONTACT_INFO: '4064',
  CANCEL_BILLING_CONTACT_INFO: '4063',
  VIEW_RENTALS_BILLING_INVOICE: '6856',
} as const;

// https://dataportal.zillowgroup.net/event-registry/2821
export const emitDownloadInvoicesEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.DOWNLOAD_INVOICES,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'billing_portal|invoice',
      trigger_source_nm: 'link_to_download_invoice_pdf',
      trigger_location_nm: 'billing_portal',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_download_invoice_pdf',
      topic_tag_txt: ['invoice', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2835
export const emitDownloadCSVEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.DOWNLOAD_CSV,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'billing_portal|invoice',
      trigger_source_nm: 'link_to_download_invoice_csv',
      trigger_location_nm: 'billing_portal',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_download_invoice_csv',
      topic_tag_txt: ['invoice', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2836
export const emitSetFilterEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.SET_FILTER,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'billing_portal|payment_due',
      trigger_source_nm: 'button_to_set_filters',
      trigger_location_nm: 'billing_portal',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_set_filter',
      topic_tag_txt: ['invoice', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2840
export const emitSearchByInvoiceNumberEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.SEARCH_BY_INVOICE_NUMBER,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'billing_portal|header',
      trigger_source_nm: 'search_bar',
      trigger_location_nm: 'billing_portal',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_search',
      topic_tag_txt: ['invoice', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3298
export const emitRemovePaymentMethodEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.REMOVE_PAYMENT_METHOD,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal',
      trigger_source_nm: 'remove_payment_method',
      trigger_object_nm: 'manage_payment_details',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_remove_payment_method',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3178
export const emitAddCreditCardEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.ADD_CREDIT_CARD,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal',
      trigger_source_nm: 'add_credit_card',
      trigger_object_nm: 'add_payment_method_pop_up',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_add_credit_card',
      topic_tag_txt: ['zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3198
export const emitAddACHEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.ADD_ACH,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal',
      trigger_source_nm: 'add_ach',
      trigger_object_nm: 'add_payment_method_pop_up',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_add_ach',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3288
export const emitAddAutopayEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.ADD_AUTOPAY,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal',
      trigger_source_nm: 'toggle_on_autopay',
      trigger_object_nm: 'manage_payment_details',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_add_autopay',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3310
export const emitPayOfflineEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.PAY_OFFLINE,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal',
      trigger_source_nm: 'learn_more_options',
      trigger_object_nm: 'payment_settings',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_learn_more_offline_payments',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3292
export const emitRemoveAutopayEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.REMOVE_AUTOPAY,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal',
      trigger_source_nm: 'toggle_off_autopay',
      trigger_object_nm: 'manage_payment_details',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_remove_autopay',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3300
export const emitOtherAmountEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.OTHER_AMOUNT,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal',
      trigger_source_nm: 'pay_other_amount',
      trigger_object_nm: 'make_a_payment',
    },
    semantic: {
      semantic_event_nm: 'billing_portal_other_amount',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4062
export const emitEditBillingContactInfoEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.EDIT_BILLING_CONTACT_INFO,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal_payment_settings',
      trigger_source_nm: 'edit',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'edit_billing_info',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4064
export const emitSaveCloseBillingContactInfoEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.SAVE_CLOSE_BILLING_CONTACT_INFO,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal_payment_settings_info_modal',
      trigger_source_nm: 'button_to_save_and_close',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'save_billing_change',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4063
export const emitCancelBillingContactInfoEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.CANCEL_BILLING_CONTACT_INFO,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'billing_portal_payment_settings_info_modal',
      trigger_source_nm: 'button_to_cancel',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'cancel_billing_change',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

//https://dataportal.zillowgroup.net/event-registry/6856
export const emitViewRentalsBillingInvoiceEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: EVENT_IDS.VIEW_RENTALS_BILLING_INVOICE,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'view',
      trigger_location_nm: 'billing_invoice_page',
      trigger_source_nm: 'billing_invoice_page',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });
